/**
 * Optimized HomeApps Component
 * Performance improvements:
 * - React.memo for preventing unnecessary re-renders
 * - useMemo for static config
 * - useCallback for event handlers
 * - Intersection Observer for view tracking
 * - Analytics tracking for views and clicks
 * - Next.js Image component for optimized image loading
 */

'use client';

import { memo, useMemo, useCallback, useRef, useEffect } from 'react';
import Image from 'next/image';
import { homeAppsConfig } from "@/constant/home";
import { useIntersectionObserver, useAnalytics } from '@/hooks/usePerformance';

// Memoized app card component
const AppCard = memo(function AppCard({
    app,
    index,
}: {
    app: typeof homeAppsConfig.homeAppList[0];
    index: number;
}) {
    const cardRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Track card visibility
    const isInView = useIntersectionObserver(cardRef, {
        threshold: 0.3,
        rootMargin: '50px',
    });

    // Track when card becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('app_card_view', {
                app_name: app.heading,
                app_index: index,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, app.heading, index, trackEvent]);

    // Track card click
    const handleCardClick = useCallback(() => {
        trackEvent('app_card_click', {
            app_name: app.heading,
            app_index: index,
        });
    }, [app.heading, index, trackEvent]);

    return (
        <div
            ref={cardRef}
            className="home_apps_item p-4 rounded-lg bg-[var(--primary--beige)] dark:bg-[var(--gray--100)] cursor-pointer"
            onClick={handleCardClick}
            role="article"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick();
                }
            }}
        >
            <div className="home_apps_top-content">
                <div className="home_apps_content">
                    <div className="margin-bottom margin-xxsmall">
                        <h3 className="heading-xsmall">{app.heading}</h3>
                    </div>
                    <p dangerouslySetInnerHTML={{ __html: app.description }} />
                </div>
            </div>
            <div className="apps_image-wrapper">
                <Image
                    src={app.image}
                    alt={app.imageAlt}
                    width={600}
                    height={400}
                    className="apps_image"
                    loading="lazy"
                />
            </div>
        </div>
    );
});

function HomeAppsComponent() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Memoize config to prevent re-creation
    const config = useMemo(() => homeAppsConfig, []);

    // Track section visibility
    const isInView = useIntersectionObserver(sectionRef, {
        threshold: 0.2,
        rootMargin: '50px',
    });

    // Track when section becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('home_apps_section_view', {
                section_title: config.headline.replace(/<[^>]*>/g, ''), // Strip HTML
                total_apps: config.homeAppList.length,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, config.headline, config.homeAppList.length, trackEvent]);

    return (
        <div ref={sectionRef} className="section_home-apps">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-vertical padding-xhuge">
                        <div className="max-width-large align-center">
                            <div className="text-align-center">
                                <p
                                    className="text-as-h2 text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]"
                                    dangerouslySetInnerHTML={{ __html: config.headline }}
                                />
                                <div className="margin-top margin-xsmall">
                                    <p
                                        className="text-size-medium"
                                        dangerouslySetInnerHTML={{ __html: config.subheading }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="margin-top margin-xlarge">
                            <div className="home_apps_component">
                                {config.homeAppList.map((app, idx) => (
                                    <AppCard key={`home-apps-${idx}`} app={app} index={idx} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Export memoized component
export const HomeApps = memo(HomeAppsComponent);
HomeApps.displayName = 'HomeApps';

// Default export for backward compatibility
export default HomeApps;
